/**
 * Notification-specific variable definitions registered into the shared
 * template variable registry.
 *
 * These supplement the base organization variables already in the registry
 * with patient, provider, appointment, and notification-specific fields
 * commonly used in email templates.
 *
 * At runtime, values are resolved from the triggering resource via
 * resourcePath mapping in the per-template variable config. The registry
 * definitions here provide the picker catalog only — resolution uses the
 * notification template renderer, not the registry's resolve functions.
 */
import { registerVariables, type VariableDefinition } from '@/lib/template-variables/registry';

const notificationVariables: VariableDefinition[] = [
  // Patient
  {
    id: 'patient.firstName',
    label: 'Patient first name',
    description: 'First/given name of the patient.',
    resolve: (ctx) => ctx.custom?.['patient.firstName'],
  },
  {
    id: 'patient.lastName',
    label: 'Patient last name',
    description: 'Last/family name of the patient.',
    resolve: (ctx) => ctx.custom?.['patient.lastName'],
  },
  {
    id: 'patient.fullName',
    label: 'Patient full name',
    description: 'Full display name of the patient.',
    resolve: (ctx) => ctx.custom?.['patient.fullName'],
  },
  {
    id: 'patient.email',
    label: 'Patient email',
    description: 'Primary email address of the patient.',
    resolve: (ctx) => ctx.custom?.['patient.email'],
  },

  // Provider / Practitioner
  {
    id: 'provider.name',
    label: 'Provider name',
    description: 'Full display name of the provider/practitioner.',
    resolve: (ctx) => ctx.custom?.['provider.name'],
  },
  {
    id: 'provider.firstName',
    label: 'Provider first name',
    description: 'First/given name of the provider.',
    resolve: (ctx) => ctx.custom?.['provider.firstName'],
  },
  {
    id: 'provider.credentials',
    label: 'Provider credentials',
    description: 'Professional credentials (e.g. LCSW, PhD, MD).',
    resolve: (ctx) => ctx.custom?.['provider.credentials'],
  },

  // Appointment
  {
    id: 'appointment.date',
    label: 'Appointment date',
    description: 'Scheduled date of the appointment.',
    resolve: (ctx) => ctx.custom?.['appointment.date'],
  },
  {
    id: 'appointment.time',
    label: 'Appointment time',
    description: 'Scheduled time of the appointment.',
    resolve: (ctx) => ctx.custom?.['appointment.time'],
  },
  {
    id: 'appointment.type',
    label: 'Appointment type',
    description: 'Type/category of the appointment (e.g. Intake, Follow-up).',
    resolve: (ctx) => ctx.custom?.['appointment.type'],
  },
  {
    id: 'appointment.location',
    label: 'Appointment location',
    description: 'Location or virtual meeting link for the appointment.',
    resolve: (ctx) => ctx.custom?.['appointment.location'],
  },

  // Episode / Care context
  {
    id: 'episode.status',
    label: 'Episode status',
    description: 'Current status of the episode of care.',
    resolve: (ctx) => ctx.custom?.['episode.status'],
  },

  // Task
  {
    id: 'task.description',
    label: 'Task description',
    description: 'Description of the task.',
    resolve: (ctx) => ctx.custom?.['task.description'],
  },
  {
    id: 'task.status',
    label: 'Task status',
    description: 'Current status of the task.',
    resolve: (ctx) => ctx.custom?.['task.status'],
  },
];

let registered = false;

/**
 * Register notification-specific variables into the shared registry.
 * Safe to call multiple times; only registers once.
 */
export function registerNotificationVariables(): void {
  if (registered) return;
  registerVariables(notificationVariables);
  registered = true;
}
